import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import type { ScheduleReportShift, SortField, SortOrder } from "./types";

export const formatTimestampText = (date: DateTime | string): string => {
	const dateObj = typeof date === "string" ? DateTime.fromISO(date) : date;
	return `${dateObj.toFormat("ccc MMM d, yyyy")} ${dateObj.toFormat("h:mma")}`;
};

export const formatTimestamp = (date: DateTime | string): ReactNode => {
	const dateObj = typeof date === "string" ? DateTime.fromISO(date) : date;
	const datePart = dateObj.toFormat("ccc MMM d, yyyy");
	const timePart = dateObj.toFormat("h:mma");

	return (
		<Box component="span">
			<Typography component="span" sx={{ opacity: 1, ml: 1 }}>
				{datePart}
			</Typography>
			<Typography component="span" sx={{ opacity: 0.5, ml: 1 }}>
				{timePart}
			</Typography>
		</Box>
	);
};

export const sortEntries = (entries: ScheduleReportShift[], sortField: SortField, sortOrder: SortOrder): ScheduleReportShift[] => {
	return [...entries].sort((a, b) => {
		const compareValue = sortOrder === "asc" ? 1 : -1;

		if (sortField === "startTime" || sortField === "endTime") {
			const aDate = a[sortField] as DateTime;
			const bDate = b[sortField] as DateTime;
			return aDate.diff(bDate, "minute").minutes * compareValue;
		}

		if (sortField === "charge") {
			return (Number(a.charge) - Number(b.charge)) * compareValue;
		}

		if (sortField === "nurseName" || sortField === "role" || sortField === "employmentType" || sortField === "location") {
			const aValue = (a[sortField] ?? "") as string;
			const bValue = (b[sortField] ?? "") as string;
			return (aValue > bValue ? 1 : -1) * compareValue;
		}

		return ((a[sortField] ?? "") > (b[sortField] ?? "") ? 1 : -1) * compareValue;
	});
};

export const filterEntriesByName = (entries: ScheduleReportShift[], nameFilterEmployeeIds: number[]): ScheduleReportShift[] => {
	if (nameFilterEmployeeIds.length === 0) return entries;
	return entries.filter((entry) => nameFilterEmployeeIds.includes(entry.nurseId));
};

const MINUTES_IN_DAY = 1440;
const MIN_OVERLAP_RATIO = 0.5;

/**
 * Checks if a shift has at least 50% overlap with the selected time filter window.
 * Handles overnight shifts and overnight filter ranges by checking the filter
 * at three 24h-shifted positions on the timeline.
 */
export const hasTimeOverlap = (shiftStart: DateTime, shiftEnd: DateTime, filterStartMinutes: number, filterEndMinutes: number): boolean => {
	const shiftDuration = shiftEnd.diff(shiftStart, "minutes").minutes;
	if (shiftDuration <= 0) return false;

	const sStart = shiftStart.hour * 60 + shiftStart.minute;
	const sEnd = sStart + shiftDuration;

	let fEnd = filterEndMinutes;
	if (fEnd <= filterStartMinutes) fEnd += MINUTES_IN_DAY;

	const getOverlap = (fStart: number, fEndAdj: number): number => Math.max(0, Math.min(sEnd, fEndAdj) - Math.max(sStart, fStart));

	const overlap = Math.max(
		getOverlap(filterStartMinutes, fEnd),
		getOverlap(filterStartMinutes - MINUTES_IN_DAY, fEnd - MINUTES_IN_DAY),
		getOverlap(filterStartMinutes + MINUTES_IN_DAY, fEnd + MINUTES_IN_DAY),
	);

	return overlap / shiftDuration >= MIN_OVERLAP_RATIO;
};
